import { Badge, Button, Card, Input, LoadingContainer, Modal, Select } from '@/components/ui';
import {
  useAuditoria,
  useEmpresasSysadmin,
  useRecalcularCumplimiento,
  useSincronizarCumplimiento,
  useExportComplianceData,
  useImportComplianceData,
} from '@/hooks/useSysadmin';
import { DashboardEmpresaAsesoriaUpdate } from '@/types/types';
import { getAsesoriaEstadoBadgeVariant, getAsesoriaEstadoLabel, normalizeAsesoriaEstado } from '@/utils/asesoriaStatus';
import { FileEdit, RefreshCw, Search, ShieldCheck, Download, Upload } from 'lucide-react';
import { useState, useRef } from 'react';

export function GestionAuditoriaPage() {
  const { empresas, isLoading: loadingEmpresas } = useEmpresasSysadmin();
  const { empresaDetail, loadingDetail, fetchDetail, actualizarAsesoria, error: auditoriaError } = useAuditoria();
  const { isLoading: isRecalculando, recalcular, error: recalcularError } = useRecalcularCumplimiento();
  const { isLoading: isSincronizando, sincronizar, error: sincronizarError } = useSincronizarCumplimiento();
  const { isLoading: isExporting, exportData } = useExportComplianceData();
  const { isLoading: isImporting, importData } = useImportComplianceData();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [selectedNit, setSelectedNit] = useState('');

  const [showDropdown, setShowDropdown] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Modals state
  const [editAsesoria, setEditAsesoria] = useState<DashboardEmpresaAsesoriaUpdate | null>(null);
  const [saving, setSaving] = useState(false);

  const filteredEmpresas = empresas
    .filter((e) => e.nombre.toLowerCase().includes(search.toLowerCase()) || e.nitId.includes(search))
    .slice(0, 20);

  const selectedEmpresa = empresas.find((e) => e.nitId === selectedNit);

  const handleSelectEmpresa = (nit: string, nombre: string) => {
    setSelectedNit(nit);
    setSearch(nombre);
    setShowDropdown(false);
    setShowAll(false);
    fetchDetail(nit);
  };

  const clearSelection = () => {
    setSelectedNit('');
    setSearch('');
    setShowDropdown(false);
    setShowAll(false);
  };

  const handleExport = async () => {
    try {
      const data = await exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cumplimiento_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error al exportar');
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) {
        throw new Error('El archivo no tiene el formato correcto (debe ser un array de objetos).');
      }

      const ok = window.confirm(`Se importarán ${data.length} registros. ¿Desea continuar?`);
      if (!ok) return;

      const updated = await importData(data);
      window.alert(`Importación completada. Registros actualizados/creados: ${updated}`);

      if (selectedNit) {
        fetchDetail(selectedNit);
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUpdateAsesoria = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editAsesoria) return;
    setSaving(true);
    try {
      const formData = new FormData(e.currentTarget as HTMLFormElement);
      const data = {
        objetivo: formData.get('objetivo'),
        horas: parseFloat(formData.get('horas') as string),
        recomendaciones: formData.get('recomendaciones'),
        estado: formData.get('estado'),
        fechaInicio: new Date(formData.get('fechaInicio') as string).toISOString(),
        fechaFin: formData.get('fechaFin') ? new Date(formData.get('fechaFin') as string).toISOString() : null,
      };
      await actualizarAsesoria(parseInt(editAsesoria.asesoriaId), data);
      await fetchDetail(selectedNit);
      setEditAsesoria(null);
    } catch (err) {
      alert('Error al actualizar asesoría');
    } finally {
      setSaving(false);
    }
  };

  if (loadingEmpresas) return <LoadingContainer text="Cargando empresas..." />;

  const error = auditoriaError || recalcularError || sincronizarError;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex-1 max-w-2xl">
          <div className="space-y-2 relative">
            <label className="text-sm font-medium text-gray-700 flex justify-between">
              <span>Buscar y Seleccionar Empresa</span>
              {selectedNit && (
                <button onClick={clearSelection} className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  Limpiar selección
                </button>
              )}
            </label>
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${selectedNit ? 'text-blue-500' : 'text-gray-400'}`}
              />
              <Input
                placeholder="Escriba NIT o Nombre de la empresa..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!showDropdown) setShowDropdown(true);
                  if (selectedNit && e.target.value !== selectedEmpresa?.nombre) {
                    setSelectedNit('');
                  }
                  if (showAll) setShowAll(false);
                }}
                onFocus={() => setShowDropdown(true)}
                className="pl-9 pr-10"
              />
              {selectedNit && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <ShieldCheck className="w-4 h-4 text-green-500" />
                </div>
              )}
            </div>

            {/* Dropdown de resultados */}
            {showDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
                <Card className="absolute left-0 right-0 top-full mt-1 z-20 max-h-72 overflow-y-auto shadow-2xl border-blue-100 animate-in fade-in slide-in-from-top-2 duration-200 ring-1 ring-black/5">
                  <div className="p-1">
                    {search.length === 0 ? (
                      /* Lista inicial: Todas las empresas (primeras 50 o todas si showAll es true) */
                      (showAll ? empresas : empresas.slice(0, 50)).map((e) => (
                        <button
                          key={e.nitId}
                          onClick={() => handleSelectEmpresa(e.nitId, e.nombre)}
                          className={`w-full text-left px-3 py-2.5 rounded-md transition-colors flex flex-col gap-0.5
                            ${selectedNit === e.nitId ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <span className={`text-sm font-medium ${selectedNit === e.nitId ? 'text-blue-700' : 'text-gray-900'}`}>
                            {e.nombre}
                          </span>
                          <span className="text-xs text-gray-500">NIT: {e.nitId}</span>
                        </button>
                      ))
                    ) : filteredEmpresas.length > 0 ? (
                      filteredEmpresas.map((e) => (
                        <button
                          key={e.nitId}
                          onClick={() => handleSelectEmpresa(e.nitId, e.nombre)}
                          className={`w-full text-left px-3 py-2.5 rounded-md transition-colors flex flex-col gap-0.5
                            ${selectedNit === e.nitId ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                        >
                          <span className={`text-sm font-medium ${selectedNit === e.nitId ? 'text-blue-700' : 'text-gray-900'}`}>
                            {e.nombre}
                          </span>
                          <span className="text-xs text-gray-500">NIT: {e.nitId}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-6 text-center">
                        <p className="text-sm text-gray-500">No se encontraron empresas que coincidan con "{search}"</p>
                        <button
                          onClick={() => {
                            setSearch('');
                            setShowAll(true);
                          }}
                          className="text-xs text-blue-600 mt-2 font-medium hover:underline"
                        >
                          Ver todas las empresas
                        </button>
                      </div>
                    )}
                    {search.length === 0 && !showAll && empresas.length > 50 && (
                      <div className="px-3 py-2 text-center border-t border-gray-50">
                        <button
                          onClick={() => setShowAll(true)}
                          className="text-[10px] text-blue-600 uppercase tracking-widest font-bold hover:underline"
                        >
                          Mostrando 50 de {empresas.length}. Clic aquí para ver todas.
                        </button>
                      </div>
                    )}
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap justify-end">
          <input type="file" accept=".json" ref={fileInputRef} onChange={handleImport} className="hidden" />
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition shadow-sm ${
              isExporting
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-white text-blue-600 border border-blue-200 hover:bg-blue-50 hover:border-blue-300 active:bg-blue-100'
            }`}
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} />
            {isExporting ? 'Exportando…' : 'Exportar'}
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition shadow-sm ${
              isImporting
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-white text-emerald-600 border border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 active:bg-emerald-100'
            }`}
          >
            <Upload className={`w-4 h-4 ${isImporting ? 'animate-pulse' : ''}`} />
            {isImporting ? 'Importando…' : 'Importar'}
          </button>

          <button
            type="button"
            onClick={async () => {
              const ok = window.confirm(
                '¿Sincronizar estados de requisitos a partir del historial de asesorías? Se reconstruirá el cumplimiento para todas las empresas.'
              );
              if (!ok) return;
              try {
                const processed = await sincronizar();
                window.alert(`Sincronización completada. Empresas procesadas: ${processed}`);
              } catch (err) {
                window.alert(err instanceof Error ? err.message : 'Error al sincronizar cumplimiento');
              }
            }}
            disabled={isSincronizando}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition shadow-sm ${
              isSincronizando
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-white text-orange-600 border border-orange-200 hover:bg-orange-50 hover:border-orange-300 active:bg-orange-100'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isSincronizando ? 'animate-spin' : ''}`} />
            {isSincronizando ? 'Sincronizando…' : 'Sincronizar desde Asesorías'}
          </button>

          <button
            type="button"
            onClick={async () => {
              const ok = window.confirm('¿Recalcular porcentajeCumplimiento para todas las NTCs? Esta operación puede tardar.');
              if (!ok) return;
              try {
                const updated = await recalcular();
                window.alert(`Recalculo completado. Registros actualizados: ${updated}`);
              } catch (err) {
                window.alert(err instanceof Error ? err.message : 'Error al recalcular cumplimiento');
              }
            }}
            disabled={isRecalculando}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition shadow-sm ${
              isRecalculando
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                : 'bg-white text-red-600 border border-red-200 hover:bg-red-50 hover:border-red-300 active:bg-red-100'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isRecalculando ? 'animate-spin' : ''}`} />
            {isRecalculando ? 'Recalculando…' : 'Recalcular Porcentajes'}
          </button>
        </div>
      </div>

      {error && (
        <Card className="p-4 bg-red-50 border-red-100 flex items-center gap-3 text-red-700">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <p className="text-sm font-medium">{error}</p>
        </Card>
      )}

      {loadingDetail && (
        <div className="py-12">
          <LoadingContainer text="Cargando detalles de auditoría..." />
        </div>
      )}

      {selectedNit && !empresaDetail && !loadingDetail && !error && (
        <Card className="p-8 text-center bg-gray-50 border-dashed border-2">
          <p className="text-gray-500">Seleccione una empresa para ver su historial de auditoría.</p>
        </Card>
      )}

      {empresaDetail && !loadingDetail && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Asesorías */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <h2>Historial de Asesorías</h2>
              </div>
              <Badge variant="blue">{empresaDetail.asesorias.length} Registros</Badge>
            </div>
            <Card className="overflow-hidden border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase tracking-wider">Período</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 uppercase tracking-wider">Objetivo</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600 uppercase tracking-wider">Horas</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600 uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {empresaDetail.asesorias.length > 0 ? (
                      empresaDetail.asesorias.map((a) => (
                        <tr key={a.asesoriaId} className="hover:bg-blue-50/30 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-gray-900 font-medium">{new Date(a.fechaInicio).toLocaleDateString()}</div>
                            <div className="text-gray-500 text-xs italic">
                              {a.fechaFin ? new Date(a.fechaFin).toLocaleDateString() : 'En curso'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-md">
                            <div
                              className="line-clamp-2"
                              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                            >
                              {a.objetivo}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-mono font-medium text-blue-600">{a.horas}h</td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant={getAsesoriaEstadoBadgeVariant(normalizeAsesoriaEstado({ estado: a.estado }))}>
                              {getAsesoriaEstadoLabel(normalizeAsesoriaEstado({ estado: a.estado }))}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditAsesoria(a)}
                              className="hover:text-blue-700 hover:bg-blue-100"
                            >
                              <FileEdit className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-400 italic">
                          No hay asesorías registradas para esta empresa.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </section>

          {/* Placeholder para Audios y otras secciones si se requieren */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
              <Search className="w-4 h-4" />
            </div>
            <p className="text-blue-800 text-sm font-medium">
              Próximamente: Edición de grabaciones de audio y cuestionarios de evaluación.
            </p>
          </div>
        </div>
      )}

      {/* Modal Edición de Asesoría */}
      <Modal
        isOpen={!!editAsesoria}
        onClose={() => setEditAsesoria(null)}
        title={editAsesoria ? `Editar Asesoría #${editAsesoria.asesoriaId}` : ''}
      >
        <form onSubmit={handleUpdateAsesoria} className="space-y-5 p-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha de Inicio</label>
              <Input type="date" name="fechaInicio" defaultValue={editAsesoria?.fechaInicio.split('T')[0]} required />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha de Finalización</label>
              <Input type="date" name="fechaFin" defaultValue={editAsesoria?.fechaFin?.split('T')[0]} />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Objetivo de la Asesoría</label>
            <Input name="objetivo" defaultValue={editAsesoria?.objetivo} required placeholder="Describa el objetivo principal..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Horas Realizadas</label>
              <Input type="number" step="0.5" name="horas" defaultValue={editAsesoria?.horas} required />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Estado Actual</label>
              <Select
                name="estado"
                defaultValue={editAsesoria?.estado}
                options={[
                  { value: 'EN_CURSO', label: 'En Curso' },
                  { value: 'EN_REVISION', label: 'En revisión' },
                  { value: 'FINALIZADA', label: 'Finalizada' },
                ]}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Recomendaciones y Observaciones</label>
            <textarea
              name="recomendaciones"
              className="w-full border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none rounded-lg p-3 text-sm min-h-32 bg-white transition-all"
              defaultValue={editAsesoria?.recomendaciones}
              placeholder="Escriba las recomendaciones para la empresa..."
            />
          </div>
          <div className="flex gap-3 justify-end pt-5">
            <Button variant="secondary" onClick={() => setEditAsesoria(null)} type="button">
              Cancelar
            </Button>
            <Button type="submit" isLoading={saving} className="min-w-32">
              Guardar Cambios
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
